import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PROFILE_TYPE_LABEL } from "@/lib/constants";
import { locationLine } from "@/lib/format";
import PortfolioButtons from "@/components/shared/PortfolioButtons";
import ProfileTagSection from "@/components/shared/ProfileTagSection";
import ReviewsSection from "@/components/shared/ReviewsSection";
import GigCard from "@/components/shared/GigCard";
import EmptyState from "@/components/shared/EmptyState";
import { MapPin, CheckCircle2, Lock } from "lucide-react";

// Temporary feature flag: set to true to show the email-verification card again.
const SHOW_EMAIL_VERIFICATION = false;

export default function ProfileDetail() {
  const { id } = useParams();
  const { account } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [liveGigs, setLiveGigs] = useState([]);

  useEffect(() => {
    api.get(`/profiles/${id}`).then(({ data }) => {
      setP(data);
      if (data.profile_type === "venue" || data.profile_type === "band") {
        api.get(`/opportunities?profile_id=${id}`).then(({ data: ops }) => {
          setLiveGigs((ops || []).filter((o) => o.is_live));
        }).catch(() => setLiveGigs([]));
      }
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <AppLayout><div className="py-20 text-center text-slate-500">Profile not found.</div></AppLayout>;
  if (!p) return <AppLayout><div className="py-20 text-center text-slate-500">Loading…</div></AppLayout>;

  const isOwner = account?.user_id === p.user_id;
  const isVenue = p.profile_type === "venue";
  const isBand = p.profile_type === "band";
  const showsGigs = isVenue || isBand;

  return (
    <AppLayout>
      <button onClick={() => nav(-1)} className="text-sm text-slate-500 hover:text-slate-900">← Back</button>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <Card className="p-8 border-slate-200">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <img src={p.profile_picture_url} alt={p.display_name}
              className="w-28 h-28 rounded-xl object-cover bg-slate-100 flex-none" data-testid="profile-picture" />
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-3xl font-bold tracking-tight">{p.display_name}</h1>
              <div className="mt-1 text-slate-600 text-sm inline-flex items-center gap-1">
                {PROFILE_TYPE_LABEL[p.profile_type]} · <MapPin className="w-3.5 h-3.5" /> {locationLine(p) || "—"}
                {isVenue && p.venue_type ? ` · ${p.venue_type}` : ""}
              </div>
              {showsGigs && (
                <div className="mt-2 text-xs text-slate-500" data-testid="gig-count">
                  <b className="text-slate-900">{p.gigs_posted || 0}</b> Gigs Posted
                </div>
              )}
              <div className="mt-4">
                <PortfolioButtons profile={p} />
              </div>
            </div>
          </div>

          <ProfileTagSection heading="Genres" items={p.genres} />
          <ProfileTagSection heading="Instruments" items={p.instruments} tone="accent" />
          <ProfileTagSection heading="Gig Types" items={p.gig_types} />

          <section className="mt-8">
            <h3 className="font-semibold">About</h3>
            <p className="mt-2 text-slate-700 leading-relaxed">{p.bio || "No bio yet."}</p>
          </section>

          {showsGigs && (
            <section className="mt-8" data-testid="live-gigs">
              <h3 className="font-display text-xl font-semibold tracking-tight">Live Gigs</h3>
              {liveGigs.length === 0 ? (
                <div className="mt-3 text-sm text-slate-500">No active gigs right now.</div>
              ) : (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {liveGigs.map((g) => <GigCard key={g.opportunity_id} opportunity={g} />)}
                </div>
              )}
            </section>
          )}

          <ReviewsSection profileId={p.profile_id} />
        </Card>

        <aside className="space-y-4">
          <Card className="p-6 border-slate-200">
            <div className="text-xs uppercase text-slate-500 tracking-wider">
              {isVenue ? "Capacity" : "Profile"}
            </div>
            <div className="mt-1 font-display text-2xl font-bold">
              {isVenue ? (p.capacity ? `${p.capacity} guests` : "—") : PROFILE_TYPE_LABEL[p.profile_type]}
            </div>
            {isOwner ? (
              <Button onClick={() => nav(`/profiles/${p.profile_id}/edit`)} data-testid="edit-my-profile"
                className="w-full mt-4 bg-[#0F172A] hover:bg-[#1E293B] text-white">Edit profile</Button>
            ) : (
              <div className="mt-4 flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <Lock className="w-4 h-4 text-slate-500" />
                Contact locked. Apply through a gig — details unlock once accepted.
              </div>
            )}
          </Card>

          {SHOW_EMAIL_VERIFICATION && (
            <Card className="p-6 border-slate-200">
              <div className="text-xs uppercase text-slate-500 tracking-wider">Verification</div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" /> Email verified
                </div>
              </div>
            </Card>
          )}
        </aside>
      </div>
    </AppLayout>
  );
}
